import {useMutation} from '@apollo/client'
import React, {useCallback, useEffect, useState} from 'react'
import {Alert, Button, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {HARD_DELETE_COMMISSION_REPORT} from '../../../gql/mutations/commissionReportMutations'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {IResHardDeleteCommissionReport} from '../../../types/commissionReportApi'

interface Props {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit: {id: string; name: string}
}

type Step = 'confirm1' | 'confirm2'

export const ModalHardDeleteCommissionReport: React.FC<Props> = ({
  infoEdit,
  isOpen,
  onClose,
  refetch,
}) => {
  const intl = useIntl()
  const [step, setStep] = useState<Step>('confirm1')
  const [gqlHardDelete, {loading, error}] = useMutation<IResHardDeleteCommissionReport>(
    HARD_DELETE_COMMISSION_REPORT
  )

  const handleClose = useCallback(() => {
    setStep('confirm1')
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) setStep('confirm1')
  }, [isOpen])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleExecute = async () => {
    try {
      const {data} = await gqlHardDelete({variables: {id: parseId(infoEdit.id)}})
      if (data?.commissionReportHardDeleteMutation?.success) {
        toast.success(intl.formatMessage({id: 'commissions.reports.hardDelete.toast.success', defaultMessage: 'Commission report permanently deleted.'}))
        refetch()
        handleClose()
      } else {
        const msg =
          data?.commissionReportHardDeleteMutation?.errors?.[0]?.message ||
          intl.formatMessage({id: 'commissions.reports.hardDelete.unable', defaultMessage: 'Unable to delete report.'})
        toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: msg}))
      }
    } catch (e: any) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: e.message}))
    }
  }

  return (
    <Modal show={isOpen} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='commissions.reports.hardDelete.title' defaultMessage='Permanently Delete Commission Report' />
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant={step === 'confirm1' ? 'warning' : 'danger'} className='mb-3'>
          <Alert.Heading>
            <i
              className={`bi ${
                step === 'confirm1'
                  ? 'bi-exclamation-triangle-fill'
                  : 'bi-exclamation-octagon-fill'
              } me-2`}
            />
            {step === 'confirm1'
              ? <FormattedMessage id='commissions.reports.hardDelete.step1Title' defaultMessage='Are you sure?' />
              : <FormattedMessage id='commissions.reports.hardDelete.step2Title' defaultMessage='Final confirmation' />}
          </Alert.Heading>
          <p className='mb-0'>
            {step === 'confirm1' ? (
              <FormattedMessage
                id='commissions.reports.hardDelete.step1Body'
                defaultMessage='You are about to <b>permanently delete</b> the report <b>"{name}"</b>. This action <b>cannot be undone</b>.'
                values={{b: (chunks: React.ReactNode) => <strong>{chunks}</strong>, name: infoEdit.name}}
              />
            ) : (
              <FormattedMessage
                id='commissions.reports.hardDelete.step2Body'
                defaultMessage='Are you <b>absolutely sure</b>? This will <b>permanently and irreversibly delete</b> the report <b>"{name}"</b> and all its associated data.'
                values={{b: (chunks: React.ReactNode) => <strong>{chunks}</strong>, name: infoEdit.name}}
              />
            )}
          </p>
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant='light'
          onClick={() => (step === 'confirm1' ? handleClose() : setStep('confirm1'))}
        >
          {step === 'confirm1'
            ? <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
            : <FormattedMessage id='commissions.reports.hardDelete.goBack' defaultMessage='Go back' />}
        </Button>
        {step === 'confirm1' ? (
          <Button variant='warning' onClick={() => setStep('confirm2')}>
            <FormattedMessage id='commissions.reports.hardDelete.yesContinue' defaultMessage='Yes, continue' />
          </Button>
        ) : (
          <Button variant='danger' onClick={handleExecute} disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            <i className='bi bi-trash-fill me-1' /> <FormattedMessage id='commissions.reports.hardDelete.yesDelete' defaultMessage='Yes, delete permanently' />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
